/** @format */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	canCancelImportJob,
	canRollBackImportJob,
	type CreateImportInput,
	type ImportJob,
} from "@question-bank/shared";
import { Locale } from "../../features/i18n/init";
import { useBrand } from "../../hooks/useBrand";
import { useImportJob } from "../../hooks/useImportJob";
import { cancelImport, createImport, rollbackImport, startImportExecution } from "../../api/imports";
import { getAssetUrl } from "../../api/questions";
import { notify } from "../../components/Toast/toast";
import { ImportDraftQuestions } from "../../components/ImportDraftQuestions";
import { ImportStageStepper } from "../../components/ImportStageStepper";
import { ImportStatusBadge } from "../../components/ImportStatusBadge";

interface ImportConfirmDialogProps {
	title: string;
	titleId: string;
	description: string;
	descriptionId: string;
	dismissLabel: string;
	confirmLabel: string;
	pendingLabel: string;
	isPending: boolean;
	onConfirm: () => void;
	onDismiss: () => void;
}

/**
 * Confirmation for a destructive import action. Focus lands on the dismiss
 * button and Tab is trapped inside the dialog. Escape dismisses unless the
 * action is in flight. The page returns focus to the trigger when it closes.
 */
const ImportConfirmDialog = ({
	title,
	titleId,
	description,
	descriptionId,
	dismissLabel,
	confirmLabel,
	pendingLabel,
	isPending,
	onConfirm,
	onDismiss,
}: ImportConfirmDialogProps) => {
	const dismissRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dismissRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isPending) {
				onDismiss();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onDismiss, isPending]);

	// Focus trap
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		const focusable = dialog.querySelectorAll<HTMLElement>(
			'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const trap = (event: KeyboardEvent) => {
			if (event.key !== "Tab") {
				return;
			}
			if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
				event.preventDefault();
				(event.shiftKey ? last : first).focus();
			}
		};
		dialog.addEventListener("keydown", trap);
		return () => dialog.removeEventListener("keydown", trap);
	}, [isPending]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
			<div
				ref={dialogRef}
				role="alertdialog"
				aria-modal="true"
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-lg"
			>
				<h2 id={titleId} className="text-base font-semibold text-slate-800">
					{title}
				</h2>
				<p id={descriptionId} className="mt-2 text-slate-600">
					{description}
				</p>
				<div className="mt-5 flex justify-end gap-3">
					<button
						ref={dismissRef}
						type="button"
						onClick={onDismiss}
						disabled={isPending}
						className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{dismissLabel}
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={isPending}
						className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
					>
						{isPending ? pendingLabel : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

/**
 * A failed or cancelled PDF job can run again from the PDFs it already stored.
 * The new job gets a new id. A Step Functions execution name is fixed to the
 * job id, so the failed execution cannot be started a second time.
 */
const retryImportInput = (job: ImportJob): Extract<CreateImportInput, { source: "pdf" }> | null => {
	if (
		(job.status !== "failed" && job.status !== "cancelled") ||
		job.source !== "pdf" ||
		!job.sourceKeys ||
		!job.paperId ||
		!job.paperCode
	) {
		return null;
	}
	return {
		source: "pdf",
		paperId: job.paperId,
		paperCode: job.paperCode,
		...(job.subject ? { subject: job.subject } : {}),
		sourceKeys: job.sourceKeys,
	};
};

export const AdminImportJobDetailsPage = () => {
	const { brand } = useBrand();
	const navigate = useNavigate();
	const { locale, id = "" } = useParams<{ locale: Locale; id: string }>();
	const { job, isLoading, error, reload } = useImportJob(brand, id);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isCancelOpen, setIsCancelOpen] = useState(false);
	const [isRollingBack, setIsRollingBack] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [shouldFocusHeading, setShouldFocusHeading] = useState(false);
	const rollbackButtonRef = useRef<HTMLButtonElement>(null);
	const cancelButtonRef = useRef<HTMLButtonElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);

	// After the trigger button is gone, focus moves to the heading once the
	// refreshed job has rendered.

	useEffect(() => {
		if (
			shouldFocusHeading &&
			(job?.status === "rolled_back" || job?.status === "failed" || job?.status === "cancelled")
		) {
			headingRef.current?.focus();
			setShouldFocusHeading(false);
		}
	}, [shouldFocusHeading, job]);

	const backLink = `/${locale}/admin/import-jobs`;

	const closeConfirm = () => {
		setIsConfirmOpen(false);
		rollbackButtonRef.current?.focus();
	};

	const closeCancel = () => {
		setIsCancelOpen(false);
		cancelButtonRef.current?.focus();
	};

	const handleRollBack = async () => {
		if (!job) {
			return;
		}
		setIsRollingBack(true);
		try {
			await rollbackImport(brand, job.id);
			setIsConfirmOpen(false);
			setShouldFocusHeading(true);
			notify("success", "Import rolled back");
			reload();
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Roll back failed. Please try again");
		} finally {
			setIsRollingBack(false);
		}
	};

	const handleCancel = async () => {
		if (!job) {
			return;
		}
		setIsCancelling(true);
		try {
			await cancelImport(brand, job.id);
			setIsCancelOpen(false);
			setShouldFocusHeading(true);
			notify("success", "Import cancelled");
			reload();
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Cancel failed. Please try again");
		} finally {
			setIsCancelling(false);
		}
	};

	const handleRetry = async () => {
		if (!job) {
			return;
		}
		const input = retryImportInput(job);
		if (!input) {
			return;
		}
		setIsRetrying(true);
		try {
			const created = await createImport(brand, input);
			await startImportExecution(brand, created.id);
			notify("success", "Import restarted");
			navigate(`/${locale}/admin/import-jobs/${created.id}`);
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Retry failed. Please try again");
		} finally {
			setIsRetrying(false);
		}
	};

	if (isLoading) {
		return (
			<section className="space-y-6">
				<p role="status" className="text-sm text-slate-600">
					Loading import job…
				</p>
			</section>
		);
	}

	if (error || !job) {
		return (
			<section className="space-y-6">
				<div role="alert" className="rounded-lg border border-red-500 bg-red-100 p-6 shadow-(--admin-shadow-raised)">
					<p className="font-semibold text-red-700"> Failed to load import job</p>
					<p className="mt-1 text-sm text-red-700">{error ?? "Import job not found"}</p>
					<Link
						to={backLink}
						className="mt-4 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
					>
						← Back to import jobs
					</Link>
				</div>
			</section>
		);
	}

	const canRollBack = canRollBackImportJob(job);
	const canCancel = canCancelImportJob(job);
	const retryInput = retryImportInput(job);
	const isStalled = (job.status === "pending" || job.status === "rolling_back") && canRollBack;
	const skippedIds = new Set(job.rollbackSkippedIds ?? []);
	const collisions = job.collisions ?? [];

	const isPdf = job.source === "pdf";
	// A PDF job has no file name and no rows. It shows the keys of its three PDFs instead.
	const pdfLink = (key: string): ReactNode => {
		const url = getAssetUrl(brand, key);
		return (
			<a
				href={url}
				target="_blank"
				rel="noreferrer"
				className="break-all text-(--admin-color-primary-strong) hover:underline"
			>
				{url}
			</a>
		);
	};
	const sourceRows: [string, ReactNode][] =
		isPdf && job.sourceKeys
			? [
					["Paper PDF", pdfLink(job.sourceKeys.paper)],
					["Mark scheme PDF", pdfLink(job.sourceKeys.markScheme)],
					["Specification PDF", pdfLink(job.sourceKeys.specification)],
					...(job.sourceKeys.paperCy
						? ([["Welsh paper PDF", pdfLink(job.sourceKeys.paperCy)]] as [string, ReactNode][])
						: []),
					...(job.sourceKeys.markSchemeCy
						? ([["Welsh mark scheme PDF", pdfLink(job.sourceKeys.markSchemeCy)]] as [string, ReactNode][])
						: []),
				]
			: [["File", job.fileName ?? "-"]];
	const detailRows: [string, ReactNode][] = [
		["Status", <ImportStatusBadge status={job.status} failedReason={job.failedReason} />],
		...sourceRows,
		["Subject", job.subject ?? "-"],
		["Paper code", job.paperCode ?? "-"],
		["Imported by", job.createdByEmail ?? job.createdBy],
		["Imported at", new Date(job.createdAt).toLocaleString("en-GB")],
		...(isPdf ? [] : ([["Rows in file", String(job.rowCount)]] as [string, ReactNode][])),
		["Questions created", String(job.createdQuestionIds.length)],
	];
	if (job.failedReason) {
		detailRows.push(["Failure reason", job.failedReason]);
	} else if (isStalled) {
		detailRows.push([
			"Failure reason",
			job.status === "rolling_back"
				? "The rollback stopped before it finished. It can be run again."
				: "The import stopped before it finished; the questions it did create can be rolled back.",
		]);
	}
	if (job.rolledBackAt) {
		detailRows.push(
			["Rolled back at", new Date(job.rolledBackAt).toLocaleString("en-GB")],
			["Rolled back by", job.rolledBackBy ?? "-"],
		);
	}
	if (skippedIds.size > 0) {
		detailRows.push(["Kept (edited, published or not yet written)", String(skippedIds.size)]);
	}
	if (collisions.length > 0) {
		detailRows.push(["Not overwritten (staged or published)", String(collisions.length)]);
	}

	return (
		<section className="space-y-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<Link to={backLink} className="text-sm font-semibold text-(--admin-color-primary-strong) hover:underline">
						← Back to import jobs
					</Link>
					<h2 ref={headingRef} tabIndex={-1} className="mt-1 text-[1.75rem] font-bold text-(--admin-color-primary)">
						Import job
					</h2>
				</div>
				{(retryInput || canRollBack || canCancel) && (
					<div className="flex flex-wrap gap-2">
						{canCancel && (
							<button
								ref={cancelButtonRef}
								type="button"
								onClick={() => setIsCancelOpen(true)}
								className="cursor-pointer rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
							>
								Cancel import
							</button>
						)}
						{retryInput && (
							<button
								type="button"
								onClick={() => void handleRetry()}
								disabled={isRetrying}
								className="cursor-pointer rounded-md bg-(--admin-color-primary) px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
							>
								{isRetrying ? "Retrying…" : "Retry import"}
							</button>
						)}
						{canRollBack && (
							<button
								ref={rollbackButtonRef}
								type="button"
								onClick={() => setIsConfirmOpen(true)}
								className="cursor-pointer rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
							>
								Roll back import
							</button>
						)}
					</div>
				)}
			</div>

			<dl className="space-y-2 rounded-lg border border-slate-200 bg-white p-6 text-sm shadow-(--admin-shadow-raised)">
				{detailRows.map(([label, value]) => (
					<div key={label} className="flex gap-4">
						<dt className="w-44 shrink-0 font-medium text-slate-500">{label}</dt>
						<dd className="text-slate-800">{value}</dd>
					</div>
				))}
			</dl>

			<ImportStageStepper job={job} />

			<ImportDraftQuestions
				brand={brand}
				locale={locale}
				jobId={job.id}
				paperId={job.paperId}
				paperCode={job.paperCode}
				createdQuestionIds={job.createdQuestionIds}
				skippedIds={skippedIds}
			/>

			{collisions.length > 0 && (
				<section aria-labelledby="collisions-heading">
					<h3 id="collisions-heading" className="text-base font-semibold text-slate-800">
						Not overwritten
					</h3>
					<p className="mt-2 text-sm text-slate-600">
						These questions are already staged or published, so this import left them unchanged.
					</p>
					<ul className="mt-2 space-y-1 text-sm">
						{collisions.map((collision) => (
							<li key={collision.questionId}>
								<code className="text-slate-800">{collision.questionId}</code>
								<span className="ml-1 text-slate-600">({collision.existingStatus})</span>
							</li>
						))}
					</ul>
				</section>
			)}

			{isConfirmOpen && (
				<ImportConfirmDialog
					title="Roll back this import?"
					titleId="rollback-dialog-title"
					description="Every question this import created will be deleted. Questions edited or published since the import are kept. This cannot be undone."
					descriptionId="rollback-dialog-desc"
					dismissLabel="Cancel"
					confirmLabel="Confirm roll back"
					pendingLabel="Rolling back…"
					isPending={isRollingBack}
					onConfirm={() => void handleRollBack()}
					onDismiss={closeConfirm}
				/>
			)}
			{isCancelOpen && (
				<ImportConfirmDialog
					title="Cancel this import?"
					titleId="cancel-import-dialog-title"
					description="The import stops. Questions it has already created stay until you roll the job back."
					descriptionId="cancel-import-dialog-desc"
					dismissLabel="Keep running"
					confirmLabel="Cancel import"
					pendingLabel="Cancelling…"
					isPending={isCancelling}
					onConfirm={() => void handleCancel()}
					onDismiss={closeCancel}
				/>
			)}
		</section>
	);
};
